import React, { useState, useEffect, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import Legend from '../components/Legend/Legend';
import Button from '../components/Button/Button';
import MapViewer from '../components/MapViewer/MapViewer';
import { MapIcon, ViewIcon, AddPlotIcon, InfoIcon, GlobeIcon, LayersIcon, CloseIcon, ChevronUpIcon } from '../components/Icon';


import styles from './Pages.module.css';

const PublicMapPage = () => {
    // Данные для легенды (загружаются с бэкенда)
    const [plotLegendData, setPlotLegendData] = useState([]);
    const [buildingLegendData, setBuildingLegendData] = useState([]);
    const [roadLegendData, setRoadLegendData] = useState([]);
    const [markerLegendData, setMarkerLegendData] = useState([]);

    // Состояние фильтров
    const [activePlotFilters, setActivePlotFilters] = useState([]);
const [activeBuildingFilters, setActiveBuildingFilters] = useState([]);
    const [activeRoadFilters, setActiveRoadFilters] = useState([]);
    const [activeMarkerFilters, setActiveMarkerFilters] = useState([]);
    const [activeStatusFilters, setActiveStatusFilters] = useState(['for_sale', 'is_building', 'is_planned', 'no_status']);
    const [mapStyleId, setMapStyleId] = useState('custom');
    const [showStatusIcons, setShowStatusIcons] = useState(true);
    const [mapData, setMapData] = useState(null);
    const [selectedFeature, setSelectedFeature] = useState(null);
    const mapRef = useRef(null);
    const previousViewState = useRef(null);

    const mapStyles = ['basic', 'satellite', 'custom'];
    const mapStyleNames = { basic: 'Схема', satellite: 'Спутник', custom: 'Своя' };
    // Генерируем SVG-строки из React-компонентов
    const mapStyleIcons = {
        basic: ReactDOMServer.renderToStaticMarkup(<MapIcon size={18} />),
        satellite: ReactDOMServer.renderToStaticMarkup(<GlobeIcon size={18} />),
        custom: ReactDOMServer.renderToStaticMarkup(<LayersIcon size={18} />)
    };
    const handleMapStyleChange = () => {
        const currentIndex = mapStyles.indexOf(mapStyleId);
        const nextIndex = (currentIndex + 1) % mapStyles.length;
        setMapStyleId(mapStyles[nextIndex]);
    };

    // Загрузка данных легенды (используем тот же API, что и админ панель)
    useEffect(() => {
        const fetchLegend = async () => {
            try {
                const response = await fetch('/api/legend');
                const legend = await response.json();

                setPlotLegendData(legend.plots || []);
                setBuildingLegendData(legend.buildings || []);
                setRoadLegendData(legend.roads || []);
                setMarkerLegendData(legend.markers || []);

                // Инициализируем фильтры - все категории активны по умолчанию
                setActivePlotFilters((legend.plots || []).map(p => p.id));
                setActiveBuildingFilters((legend.buildings || []).map(b => b.id));
                setActiveRoadFilters((legend.roads || []).map(r => r.id));
                setActiveMarkerFilters((legend.markers || []).map(m => m.id));
            } catch (error) {
                console.error('Ошибка загрузки легенды:', error);
            }
        };

        fetchLegend();
    }, []);

    // Загрузка данных карты (публичная версия - только объекты с категорией)
    useEffect(() => {
        const fetchMapData = async () => {
            try {
                const response = await fetch('/api/map');
                if (response.ok) {
                    const data = await response.json();
                    // Фильтруем: оставляем только объекты с категорией
                    const featuresWithCategory = data.features?.filter(f => f.properties?.category) || [];
                    setMapData({
                        ...data,
                        features: featuresWithCategory
                    });
                }
            } catch (error) {
                console.error('Ошибка загрузки данных карты:', error);
            }
        };

        fetchMapData();
    }, []);

    // Обработчики для легенды
    const handleToggleCategory = (id, category) => {
        // Нормализуем category: поддерживаем как русские названия, так и английские
        const normalizedCategory = category === 'участки' ? 'plots' : 
                          category === 'здания' ? 'buildings' : 
                          category === 'линии' || category === 'дороги' ? 'roads' : 
                          category === 'метки' ? 'markers' : category;
        
        switch (normalizedCategory) {
            case 'plots':
                setActivePlotFilters(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
                break;
            case 'buildings':
                setActiveBuildingFilters(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
                break;
            case 'roads':
                setActiveRoadFilters(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
                break;
            case 'markers':
                setActiveMarkerFilters(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
                break;
        }
    };

    const handleToggleStatusFilter = (status) => {
        setActiveStatusFilters(prev =>
            prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
        );
    };

    const handleSelectAll = (category) => {
        const normalizedCategory = category === 'участки' ? 'plots' : 
                          category === 'здания' ? 'buildings' : 
                          category === 'линии' || category === 'дороги' ? 'roads' : 
                          category === 'метки' ? 'markers' : category;
        switch (normalizedCategory) {
            case 'plots':
                setActivePlotFilters(plotLegendData.map(item => item.id));
                break;
            case 'buildings':
                setActiveBuildingFilters(buildingLegendData.map(item => item.id));
                break;
            case 'roads':
                setActiveRoadFilters(roadLegendData.map(item => item.id));
                break;
            case 'markers':
                setActiveMarkerFilters(markerLegendData.map(item => item.id));
                break;
        }
    };

    const handleClearAll = (category) => {
        const normalizedCategory = category === 'участки' ? 'plots' : 
                          category === 'здания' ? 'buildings' : 
                          category === 'линии' || category === 'дороги' ? 'roads' : 
                          category === 'метки' ? 'markers' : category;
        switch (normalizedCategory) {
            case 'plots': setActivePlotFilters([]); break;
            case 'buildings': setActiveBuildingFilters([]); break;
            case 'roads': setActiveRoadFilters([]); break;
            case 'markers': setActiveMarkerFilters([]); break;
        }
    };

    const handleToggleShowStatusIcons = () => {
        setShowStatusIcons(!showStatusIcons);
    };

    // Обработчики для обновления объектов
    const handleFeatureUpdate = (feature) => {
        console.log('Объект обновлен:', feature);
        // Здесь можно обновить локальное состояние или перезапросить данные
    };

    const handleFeatureDelete = (featureId) => {
        console.log('Объект удален:', featureId);
        // Здесь можно обновить локальное состояние
    };

    const handleFeatureSelect = (feature) => {
        setSelectedFeature(feature);
        
        // Сохраняем текущее состояние вида и приближаемся к объекту
        const map = mapRef.current?.getMap();
        if (map && !previousViewState.current) {
            // Сохраняем только первый раз
            previousViewState.current = {
                center: map.getCenter(),
                zoom: map.getZoom(),
                bearing: map.getBearing(),
                pitch: map.getPitch()
            };
        }
        
        if (map) {
            try {
                const turf = require('@turf/turf');
                const bbox = turf.bbox(feature);
                map.fitBounds(bbox, {
                    padding: { top: 100, bottom: 100, left: 100, right: 350 },
                    maxZoom: 18,
                    duration: 500
                });
            } catch {
                // Если не удалось получить bbox, центрируем на объекте
                const coords = feature.geometry.coordinates;
                if (feature.geometry.type === 'Point') {
                    map.flyTo({ center: coords, zoom: 17, duration: 500 });
                }
            }
        }
    };

    const handleCloseFeatureInfo = () => {
        // Возвращаемся к предыдущему состоянию
        if (previousViewState.current && mapRef.current?.getMap()) {
            const map = mapRef.current.getMap();
            map.flyTo({
                center: previousViewState.current.center,
                zoom: previousViewState.current.zoom,
                bearing: previousViewState.current.bearing,
                pitch: previousViewState.current.pitch,
                duration: 500
            });
            previousViewState.current = null;
        }
        
        setSelectedFeature(null);
    };

    // Вспомогательная функция для получения статуса
    const getStatusText = (props) => {
        const statuses = [];
        if (props.for_sale) statuses.push('В продаже');
        if (props.is_building) statuses.push('Строится');
        if (props.is_planned) statuses.push('В планах');
        return statuses.length > 0 ? statuses.join(', ') : 'Без статуса';
    };

    // Получение цвета статуса
    const getStatusColor = (props) => {
        if (props.for_sale) return '#e44d26';
        if (props.is_building) return '#ffc107';
        if (props.is_planned) return '#007bff';
        return '#cccccc';
    };

    return (
        <div className={styles.publicMapPage}>
            {/* Основная область с картой */}
            <main className={styles.mapContainer}>
                {/* Легенда в плавающем режиме */}
                <Legend
                    activePlotFilters={activePlotFilters}
                    activeBuildingFilters={activeBuildingFilters}
                    activeRoadFilters={activeRoadFilters}
                    activeMarkerFilters={activeMarkerFilters}
                    activeRoadSubtypeFilters={[]}
                    onToggleCategory={handleToggleCategory}
                    onToggleSubtype={() => {}}
                    onSelectAll={handleSelectAll}
                    onClearAll={handleClearAll}
                    showStatusIcons={showStatusIcons}
                    activeStatusFilters={activeStatusFilters}
                    onToggleShowStatusIcons={handleToggleShowStatusIcons}
                    onToggleStatusFilter={handleToggleStatusFilter} plotLegendData={plotLegendData} buildingLegendData={buildingLegendData} roadLegendData={roadLegendData} markerLegendData={markerLegendData}
                    floating={true}
                />
                {/* Карта */}
                    <MapViewer
                        mapStyleId={mapStyleId}
                        styleSwapControlProps={{
                            onClick: handleMapStyleChange,
                            title: `Стиль: ${mapStyleNames[mapStyleId]}`,
                            iconSvg: mapStyleIcons[mapStyleId]
                        }}
                        showDrawingTools={false}
                        mapData={mapData}
                        mapRef={mapRef}
                        onFeatureUpdate={handleFeatureUpdate}
                        onFeatureDelete={handleFeatureDelete}
                        onFeatureSelect={handleFeatureSelect}
                        focusedFeature={selectedFeature}
                        legendData={{
                            plots: plotLegendData,
                            buildings: buildingLegendData,
                            roads: roadLegendData,
                            markers: markerLegendData
                        }}
                        activeFilters={{
                            plots: activePlotFilters,
                            buildings: activeBuildingFilters,
                            roads: activeRoadFilters,
                            markers: activeMarkerFilters,
                            status: activeStatusFilters
                        }}
                        showStatusIcons={showStatusIcons}
                    />
                    {/* Карточка информации об объекте */}
                    {selectedFeature && (
                        <FeatureInfoCard 
                            feature={selectedFeature} 
                            onClose={handleCloseFeatureInfo}
                            legendData={{
                                plots: plotLegendData,
                                buildings: buildingLegendData,
                                roads: roadLegendData,
                                markers: markerLegendData
                            }}
                        />
                    )}
            </main>
        </div>
    );
};

export default PublicMapPage;

// Компонент карточки информации об объекте
const FeatureInfoCard = ({ feature, onClose, legendData }) => {
    const props = feature.properties || {};
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    
    // Парсинг фото - может быть JSON строкой или массивом объектов {url, caption}
    const parsePhotos = (val) => {
        if (!val) return [];
        
        // Если это просто строка (URL), возвращаем как есть
        if (typeof val === 'string' && val.startsWith('http')) {
            return [val];
        }
        
        let parsed = val;
        if (typeof val === 'string') {
            try {
                parsed = JSON.parse(val);
            } catch {
                return [val];
            }
        }
        
        if (Array.isArray(parsed)) {
            // Каждый элемент может быть объектом {url, caption} или строкой
            return parsed.map(p => {
                if (typeof p === 'string') return p;
                if (p && p.url) return p.url;
                return '';
            }).filter(Boolean);
        }
        
        return [];
    };
    
    // Парсинг ссылок - может быть JSON строкой или массивом
    const parseLinks = (val) => {
        if (!val) return [];
        if (Array.isArray(val)) return val;
        if (typeof val === 'string') {
            try {
                const parsed = JSON.parse(val);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [{ text: val, url: val }];
            }
        }
        return [];
    };
    
    const photos = parsePhotos(props.photos);
    const links = parseLinks(props.links);
    
    // Получение названия типа на русском
    const getTypeName = () => {
        if (!props.type) return '-';
        
        // Для дорог показываем категорию
        if (props.type === 'road' || props.type === 'line') {
            const categoryId = props.category;
            if (categoryId && legendData) {
                const allCategories = [
                    ...(legendData.plots || []),
                    ...(legendData.buildings || []),
                    ...(legendData.roads || []),
                    ...(legendData.markers || [])
                ];
                const found = allCategories.find(c => c.id === categoryId);
                if (found) return found.name;
            }
        }
        
        const typeNames = {
            plot: 'Участок',
            building: 'Здание',
            road: 'Дорога',
            line: 'Линия',
            marker: 'Метка'
        };
        return typeNames[props.type] || props.type;
    };
    
    const getStatusText = (p) => {
        const statuses = [];
        if (p.for_sale) statuses.push('В продаже');
        if (p.is_building) statuses.push('Строится');
        if (p.is_planned) statuses.push('В планах');
        return statuses.length > 0 ? statuses.join(', ') : 'Без статуса';
    };
    
    const getStatusColor = (p) => {
        if (p.for_sale) return '#e44d26';
        if (p.is_building) return '#ffc107';
        if (p.is_planned) return '#007bff';
        return '#cccccc';
    };
    
    const [copySuccess, setCopySuccess] = useState(false);
    
    const handleCopyCadastral = async () => {
        if (!props.cadastral_number) return;
        try {
            await navigator.clipboard.writeText(props.cadastral_number);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch {
            const textArea = document.createElement('textarea');
            textArea.value = props.cadastral_number;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };
    
    const handleNextPhoto = () => {
        setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
    };
    
    const handlePrevPhoto = () => {
        setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
    };
    
    return (
        <div className={styles.featureInfoCard}>
            <div className={styles.featureInfoCardContent}>
                <button className={styles.featureInfoCloseBtn} onClick={onClose}>
                    <CloseIcon size={20} />
                </button>
                
                <h3 className={styles.featureInfoTitle}>
                    {props.name || props.cadastral_number || 'Объект'}
                </h3>
                
                {/* Галерея фото - сразу после названия */}
                {photos.length > 0 && (
                    <div className={styles.featureInfoSection}>
                        <div className={styles.featureInfoGallery}>
                            {photos.length > 1 && (
                                <>
                                    <button 
                                        className={styles.featureInfoGalleryNav} 
                                        onClick={handlePrevPhoto}
                                        style={{ transform: 'rotate(90deg)' }}
                                    >
                                        <ChevronUpIcon size={20} />
                                    </button>
                                    <span className={styles.featureInfoGalleryCounter}>
                                        {currentPhotoIndex + 1} / {photos.length}
                                    </span>
                                </>
                            )}
                            <img 
                                src={photos[currentPhotoIndex]} 
                                alt="" 
                                className={styles.featureInfoPhoto}
                            />
                            {photos.length > 1 && (
                                <button 
                                    className={styles.featureInfoGalleryNav} 
                                    onClick={handleNextPhoto}
                                    style={{ transform: 'rotate(-90deg)' }}
                                >
                                    <ChevronUpIcon size={20} />
                                </button>
                            )}
                        </div>
                    </div>
                )}
                
                <div className={styles.featureInfoSection}>
                    <div className={styles.featureInfoRow}>
                        <span className={styles.featureInfoLabel}>Тип:</span>
                        <span className={styles.featureInfoValue}>{getTypeName()}</span>
                    </div>
                    
                    {props.cadastral_number && (
                        <div className={styles.featureInfoRow} onClick={handleCopyCadastral} style={{ cursor: 'pointer' }}>
                            <span className={styles.featureInfoLabel}>Кадастровый номер:</span>
                            <span className={styles.featureInfoValue}>
                                {copySuccess ? 'Скопировано!' : props.cadastral_number}
                            </span>
                        </div>
                    )}
                    
                    {props.area && (
                        <div className={styles.featureInfoRow}>
                            <span className={styles.featureInfoLabel}>Площадь:</span>
                            <span className={styles.featureInfoValue}>{props.area} м²</span>
                        </div>
                    )}
                    
                    <div className={styles.featureInfoRow}>
                        <span className={styles.featureInfoLabel}>Статус:</span>
                        <span 
                            className={styles.featureInfoStatus}
                            style={{ backgroundColor: getStatusColor(props) }}
                        >
                            {getStatusText(props)}
                        </span>
                    </div>
                </div>
                
                {/* Ссылки */}
                {links.length > 0 && (
                    <div className={styles.featureInfoSection}>
                        <h4 className={styles.featureInfoSectionTitle}>Ссылки</h4>
                        <div className={styles.featureInfoLinks}>
                            {links.map((link, index) => {
                                // Ссылка может быть строкой или объектом {text, url}
                                const linkUrl = typeof link === 'string' ? link : (link.url || link);
                                const linkText = typeof link === 'object' ? link.text : link;
                                return (
                                    <a 
                                        key={index}
                                        href={linkUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.featureInfoLink}
                                    >
                                        {linkText}
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                )}
                
                {/* Доп. информация */}
                {props.extra && (
                    <div className={styles.featureInfoSection}>
                        <h4 className={styles.featureInfoSectionTitle}>Доп. информация</h4>
                        <p className={styles.featureInfoExtra}>{props.extra}</p>
                    </div>
                )}
            </div>
        </div>
    );
};